<template>
  <div class="qr-generator-view pa-4">
    <!-- Corporate Header -->
    <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-avatar color="orange lighten-5" size="46" class="mr-3">
          <img :src="defaultLogoUrl" alt="Sunshine" class="header-logo" />
        </v-avatar>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Generador de Códigos QR
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Generación versátil de códigos QR estándar o personalizados con logotipo Sunshine, paleta de colores y fichas institucionales para impresión.
          </span>
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center mt-2 mt-sm-0">
          <v-chip outlined :color="isLogoEnabled ? 'primary' : 'grey darken-2'" small class="font-weight-bold mr-2">
            <v-icon left x-small>{{ isLogoEnabled ? 'mdi-shield-star' : 'mdi-qrcode' }}</v-icon>
            {{ isLogoEnabled ? 'Modo con Logotipo' : 'Modo Estándar (Sin Logo)' }}
          </v-chip>
          <v-chip outlined color="success" small class="font-weight-bold">
            <v-icon left x-small>mdi-check-decagram</v-icon>
            {{ isLogoEnabled ? 'Corrección Nivel H (30%)' : `Corrección Nivel ${errorCorrectionLevel}` }}
          </v-chip>
        </div>
      </v-card-title>
    </v-card>

    <!-- Prominent Mode Toggle Card (Standard vs Logo) -->
    <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
      <v-card-text class="py-3 px-5 d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center mr-3 mb-2 mb-sm-0">
          <v-icon :color="isLogoEnabled ? 'primary' : 'grey darken-3'" class="mr-2">
            {{ isLogoEnabled ? 'mdi-shield-star' : 'mdi-qrcode' }}
          </v-icon>
          <div>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
              ¿Deseas incluir logotipo en el código QR?
            </span>
            <span class="text-caption grey--text text--darken-1 d-block">
              Esta opción es totalmente opcional para adaptarse al requerimiento de cada área o usuario.
            </span>
          </div>
        </div>

        <v-btn-toggle
          v-model="isLogoEnabled"
          mandatory
          dense
          class="rounded-lg border-subtle"
        >
          <v-btn
            :value="false"
            small
            class="text-none font-weight-bold px-4"
            :color="!isLogoEnabled ? 'grey darken-3' : undefined"
            :class="{ 'white--text': !isLogoEnabled }"
          >
            <v-icon left small>mdi-qrcode</v-icon>
            QR Estándar (Sin Logo)
          </v-btn>
          <v-btn
            :value="true"
            small
            class="text-none font-weight-bold px-4"
            :color="isLogoEnabled ? 'primary' : undefined"
            :class="{ 'white--text': isLogoEnabled }"
          >
            <v-icon left small>mdi-shield-star</v-icon>
            Con Logotipo Sunshine
          </v-btn>
        </v-btn-toggle>
      </v-card-text>
    </v-card>

    <v-row dense>
      <!-- Left Column: Content and Customization Settings -->
      <v-col cols="12" md="7" lg="7">
        <!-- Card 1: Content Input -->
        <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
          <v-card-title class="py-3 px-5 d-flex align-center">
            <v-icon color="grey darken-3" small class="mr-2">mdi-form-select</v-icon>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
              1. Contenido del Código QR
            </span>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="py-4 px-5">
            <!-- Content Type Tabs -->
            <v-tabs v-model="activeTab" color="primary" dense class="mb-4 custom-tabs" show-arrows>
              <v-tab href="#text">
                <v-icon small class="mr-1">mdi-text-short</v-icon>
                Texto
              </v-tab>
              <v-tab href="#url">
                <v-icon small class="mr-1">mdi-link-variant</v-icon>
                Enlace / URL
              </v-tab>
              <v-tab href="#contact">
                <v-icon small class="mr-1">mdi-card-account-details-outline</v-icon>
                Contacto (vCard)
              </v-tab>
              <v-tab href="#wifi">
                <v-icon small class="mr-1">mdi-wifi</v-icon>
                Red Wi-Fi
              </v-tab>
              <v-tab href="#whatsapp">
                <v-icon small class="mr-1">mdi-whatsapp</v-icon>
                WhatsApp
              </v-tab>
              <v-tab href="#email">
                <v-icon small class="mr-1">mdi-email-outline</v-icon>
                Correo
              </v-tab>
            </v-tabs>

            <!-- Tab Content Items -->
            <v-tabs-items v-model="activeTab" class="bg-transparent">
              <!-- Free Text -->
              <v-tab-item value="text">
                <s-textarea
                  v-model="textInput"
                  label="Texto o Identificador *"
                  placeholder="Ingrese el texto, código de lote, instrucción o nota que contendrá el QR..."
                  :rows="4"
                  counter
                />
              </v-tab-item>

              <!-- URL / Web Link -->
              <v-tab-item value="url">
                <v-row dense>
                  <v-col cols="12">
                    <s-text
                      v-model="urlInput"
                      label="Dirección Web (URL) *"
                      placeholder="https://sunshine.com.pe"
                      prepend-inner-icon="mdi-web"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" class="d-flex align-center flex-wrap mt-1">
                    <span class="text-caption grey--text text--darken-1 mr-2">Accesos rápidos:</span>
                    <v-btn x-small text color="primary" class="text-none mr-1" @click="appendUrlPrefix('https://')">
                      + https://
                    </v-btn>
                    <v-btn x-small text color="primary" class="text-none" @click="appendUrlPrefix('http://')">
                      + http://
                    </v-btn>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Contact Card (vCard) -->
              <v-tab-item value="contact">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactFirstName"
                      label="Nombres *"
                      placeholder="Ej. Juan Carlos"
                      prepend-inner-icon="mdi-account"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactLastName"
                      label="Apellidos *"
                      placeholder="Ej. Pérez Gómez"
                      prepend-inner-icon="mdi-account-outline"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactPhone"
                      label="Teléfono Móvil *"
                      placeholder="+51 987654321"
                      prepend-inner-icon="mdi-phone"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactEmail"
                      label="Correo Electrónico"
                      placeholder="usuario@sunshine.com.pe"
                      prepend-inner-icon="mdi-email-outline"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactOrg"
                      label="Empresa / Organización"
                      placeholder="SUNSHINE EXPORT S.A.C."
                      prepend-inner-icon="mdi-domain"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="contactCargo"
                      label="Cargo / Puesto"
                      placeholder="Ej. Jefe de Operaciones"
                      prepend-inner-icon="mdi-badge-account-horizontal"
                    />
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Wi-Fi Network -->
              <v-tab-item value="wifi">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <s-text
                      v-model="wifiSSID"
                      label="Nombre de la Red (SSID) *"
                      placeholder="Sunshine-Visitas"
                      prepend-inner-icon="mdi-wifi"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <s-select
                      v-model="wifiSecurity"
                      label="Tipo de Seguridad"
                      :items="wifiSecurityOptions"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" sm="8" v-if="wifiSecurity !== 'nopass'">
                    <s-text
                      v-model="wifiPassword"
                      label="Contraseña de Red *"
                      :type="showWifiPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      prepend-inner-icon="mdi-lock-outline"
                    />
                  </v-col>
                  <v-col cols="12" sm="4" class="d-flex align-center" v-if="wifiSecurity !== 'nopass'">
                    <v-checkbox
                      v-model="showWifiPassword"
                      label="Mostrar clave"
                      dense
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="wifiHidden"
                      label="Red Wi-Fi Oculta"
                      dense
                      hide-details
                      class="mt-1"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- WhatsApp -->
              <v-tab-item value="whatsapp">
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <s-text
                      v-model="waCountryCode"
                      label="Código de País"
                      placeholder="51"
                      prepend-inner-icon="mdi-flag-variant-outline"
                    />
                  </v-col>
                  <v-col cols="12" sm="8">
                    <s-text
                      v-model="waPhoneNumber"
                      label="Número Telefónico *"
                      placeholder="987654321"
                      prepend-inner-icon="mdi-whatsapp"
                    />
                  </v-col>
                  <v-col cols="12">
                    <s-textarea
                      v-model="waMessage"
                      label="Mensaje Predeterminado (Opcional)"
                      placeholder="Hola, me comunico a través del código QR institucional..."
                      :rows="2"
                    />
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Email Direct -->
              <v-tab-item value="email">
                <v-row dense>
                  <v-col cols="12">
                    <s-text
                      v-model="emailAddress"
                      label="Correo de Destino *"
                      placeholder="contacto@sunshine.com.pe"
                      prepend-inner-icon="mdi-email"
                    />
                  </v-col>
                  <v-col cols="12">
                    <s-text
                      v-model="emailSubject"
                      label="Asunto"
                      placeholder="Consulta de operaciones Sunshine"
                      prepend-inner-icon="mdi-format-title"
                    />
                  </v-col>
                  <v-col cols="12">
                    <s-textarea
                      v-model="emailBody"
                      label="Cuerpo del Mensaje"
                      placeholder="Escriba el mensaje predeterminado..."
                      :rows="2"
                    />
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- Card 2: Branding & Customization Settings -->
        <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
          <v-card-title class="py-3 px-5 d-flex align-center">
            <v-icon color="grey darken-3" small class="mr-2">mdi-palette-swatch-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
              2. Personalización y Estilo del QR
            </span>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="py-4 px-5">
            <!-- SECTION A: When Logo is DISABLED (Standard Mode) -->
            <div v-if="!isLogoEnabled" class="mb-4 p-3 rounded-lg border-subtle bg-slate-subtle">
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="grey lighten-3" class="mr-2">
                    <v-icon small color="grey darken-2">mdi-qrcode</v-icon>
                  </v-avatar>
                  <div>
                    <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                      Código QR Estándar (Sin Logotipo)
                    </span>
                    <p class="text-caption grey--text text--darken-1 mb-0">
                      El código se generará de forma limpia y tradicional sin ningún icono central.
                    </p>
                  </div>
                </div>
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="rounded-lg text-none font-weight-bold mt-2 mt-sm-0"
                  @click="isLogoEnabled = true"
                >
                  <v-icon left small>mdi-shield-star</v-icon>
                  Activar Logotipo Sunshine
                </v-btn>
              </div>

              <!-- Error Correction Level for Standard Mode -->
              <v-row dense class="mt-3 pt-3 border-top-light" align="center">
                <v-col cols="12" sm="6">
                  <s-select
                    v-model="errorCorrectionLevel"
                    label="Nivel de Corrección de Errores"
                    :items="errorCorrectionOptions"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <span class="text-caption grey--text text--darken-1 d-block">
                    Para un QR estándar sin logo, el <b>Nivel Medio (M)</b> o <b>Alto (Q)</b> ofrece un equilibrio óptimo entre densidad y legibilidad.
                  </span>
                </v-col>
              </v-row>
            </div>

            <!-- SECTION B: When Logo is ENABLED -->
            <div v-else class="mb-4 p-3 rounded-lg border-subtle bg-slate-subtle">
              <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                <div class="d-flex align-center">
                  <v-avatar size="32" class="mr-2 white elevation-1">
                    <img :src="activeLogoSrc" alt="Logo preview" />
                  </v-avatar>
                  <div>
                    <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                      Logotipo Central (Activo)
                    </span>
                    <p class="text-caption grey--text text--darken-1 mb-0">
                      Incrusta la insignia corporativa en el centro del código QR
                    </p>
                  </div>
                </div>
                <v-btn
                  small
                  text
                  color="grey darken-2"
                  class="text-none font-weight-bold"
                  @click="isLogoEnabled = false"
                >
                  <v-icon left small color="error">mdi-close-circle-outline</v-icon>
                  Quitar Logotipo (Volver a Estándar)
                </v-btn>
              </div>

              <!-- Detailed Logo Settings -->
              <div class="mt-3 pt-3 border-top-light">
                <v-row dense align="center">
                  <v-col cols="12" sm="6">
                    <label class="text-caption font-weight-bold grey--text text--darken-3 d-block mb-1">
                      Origen del Logotipo
                    </label>
                    <v-btn-toggle v-model="logoSource" mandatory dense class="w-100 rounded-lg">
                      <v-btn value="default" small text class="text-none font-weight-medium flex-grow-1">
                        <v-icon left x-small color="primary">mdi-shield-star</v-icon>
                        Sunshine Oficial
                      </v-btn>
                      <v-btn value="custom" small text class="text-none font-weight-medium flex-grow-1">
                        <v-icon left x-small>mdi-upload</v-icon>
                        Personalizado
                      </v-btn>
                    </v-btn-toggle>

                    <!-- File Input for Custom Logo -->
                    <div v-if="logoSource === 'custom'" class="mt-2">
                      <input
                        type="file"
                        ref="fileLogoInput"
                        accept="image/*"
                        style="display: none"
                        @change="onCustomLogoSelected"
                      />
                      <v-btn
                        small
                        outlined
                        color="primary"
                        class="rounded-lg text-none font-weight-bold"
                        @click="$refs.fileLogoInput.click()"
                      >
                        <v-icon left small>mdi-cloud-upload</v-icon>
                        Seleccionar Imagen
                      </v-btn>
                      <span v-if="customLogoFileName" class="text-caption grey--text text--darken-2 ml-2">
                        {{ customLogoFileName }}
                      </span>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="d-flex justify-space-between align-center mb-1">
                      <span class="text-caption font-weight-bold grey--text text--darken-3">
                        Tamaño del Logo: {{ logoSizePercent }}%
                      </span>
                      <span class="text-caption grey--text text--darken-1">Por defecto: 20%</span>
                    </div>
                    <v-slider
                      v-model="logoSizePercent"
                      min="14"
                      max="26"
                      step="1"
                      color="primary"
                      hide-details
                      dense
                    ></v-slider>
                  </v-col>

                  <v-col cols="12" sm="6" class="mt-2">
                    <s-select
                      v-model="logoBadgeShape"
                      label="Forma del Fondo Protector"
                      :items="logoShapeOptions"
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>

                  <v-col cols="12" sm="6" class="mt-2 d-flex align-center">
                    <v-checkbox
                      v-model="hasLogoBorder"
                      label="Borde exterior sutil de contraste"
                      dense
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- QR Color Palette (Default: Classic Black) -->
            <div class="mb-4">
              <label class="text-caption font-weight-bold grey--text text--darken-3 d-block mb-2">
                Paleta de Color del Código QR
              </label>

              <!-- Preset Corporate Color Chips -->
              <div class="d-flex align-center flex-wrap gap-2 mb-3">
                <v-chip
                  v-for="preset in colorPresets"
                  :key="preset.hex"
                  small
                  outlined
                  :color="foregroundColor === preset.hex ? 'primary' : 'grey'"
                  class="font-weight-medium cursor-pointer"
                  :style="`border-left: 12px solid ${preset.hex} !important;`"
                  @click="applyColorPreset(preset.hex)"
                >
                  {{ preset.label }}
                </v-chip>
              </div>

              <v-row dense align="center">
                <v-col cols="12" sm="6">
                  <div class="d-flex align-center">
                    <input
                      type="color"
                      v-model="foregroundColor"
                      class="color-picker-input mr-3"
                    />
                    <div>
                      <span class="text-caption font-weight-bold grey--text text--darken-3 d-block">
                        Color de los Puntos (Frente)
                      </span>
                      <span class="text-caption text-uppercase font-mono grey--text text--darken-1">
                        {{ foregroundColor }}
                      </span>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="d-flex align-center">
                    <input
                      type="color"
                      v-model="backgroundColor"
                      class="color-picker-input mr-3"
                    />
                    <div>
                      <span class="text-caption font-weight-bold grey--text text--darken-3 d-block">
                        Color de Fondo
                      </span>
                      <span class="text-caption text-uppercase font-mono grey--text text--darken-1">
                        {{ backgroundColor }}
                      </span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Optional Presentation Card Mode -->
            <div class="p-3 rounded-lg border-subtle bg-slate-subtle mt-4">
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                    Formato de Ficha Institucional para Impresión
                  </span>
                  <p class="text-caption grey--text text--darken-1 mb-0">
                    Genera una ficha corporativa con logotipo, título y pie "SUNSHINE EXPORT S.A.C." al exportar e imprimir
                  </p>
                </div>
                <v-switch
                  v-model="isPresentationCardEnabled"
                  color="primary"
                  hide-details
                  dense
                  class="mt-0"
                ></v-switch>
              </div>

              <v-expand-transition>
                <div v-if="isPresentationCardEnabled" class="mt-3 pt-3 border-top-light">
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <s-text
                        v-model="cardTitle"
                        label="Título de la Ficha"
                        placeholder="SUNSHINE EXPORT S.A.C."
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <s-text
                        v-model="cardSubtitle"
                        label="Subtítulo / Instrucción"
                        placeholder="Escanee con su cámara móvil para acceder"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column: Live Interactive Preview & Export Actions -->
      <v-col cols="12" md="5" lg="5">
        <div class="sticky-preview-container">
          <!-- Live Preview Card -->
          <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
            <v-card-title class="py-3 px-5 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="grey darken-3" small class="mr-2">mdi-eye-outline</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
                  Vista Previa en Vivo
                </span>
              </div>
              <v-btn
                icon
                small
                color="grey darken-1"
                title="Limpiar campos"
                @click="resetAllFields"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pa-5 text-center">
              <!-- Empty State if no valid content -->
              <div v-if="!hasValidContent" class="empty-preview-placeholder py-8">
                <v-icon size="64" color="grey lighten-2">mdi-qrcode-scan</v-icon>
                <p class="text-body-2 font-weight-medium grey--text text--darken-1 mt-3 mb-1">
                  Ingrese datos para visualizar el código QR
                </p>
                <span class="text-caption grey--text">
                  Complete los campos en el panel izquierdo para generar el código.
                </span>
              </div>

              <!-- Active QR Display Container -->
              <div v-show="hasValidContent" class="qr-render-wrapper">
                <!-- Standard QR Frame or Full Presentation Card -->
                <div
                  id="printableQrSection"
                  class="qr-presentation-box mx-auto"
                  :class="{ 'card-presentation-mode': isPresentationCardEnabled }"
                >
                  <!-- Corporate Card Header (if presentation mode active) -->
                  <div v-if="isPresentationCardEnabled" class="presentation-header mb-3">
                    <img :src="activeLogoSrc" alt="Sunshine Logo" class="presentation-logo mb-2" />
                    <h3 class="text-subtitle-1 font-weight-black grey--text text--darken-4 mb-0">
                      {{ cardTitle || 'SUNSHINE EXPORT S.A.C.' }}
                    </h3>
                    <p class="text-caption grey--text text--darken-1 mb-0">
                      {{ cardSubtitle || 'Escanee con su cámara móvil para acceder' }}
                    </p>
                  </div>

                  <!-- Canvas Element -->
                  <div class="canvas-shadow-holder">
                    <canvas ref="qrCanvas" class="qr-canvas"></canvas>
                  </div>

                  <!-- Corporate Card Footer (if presentation mode active) -->
                  <div v-if="isPresentationCardEnabled" class="presentation-footer mt-3 pt-2">
                    <span class="text-caption font-weight-bold grey--text text--darken-3 text-uppercase tracking-wider">
                      SUNSHINE EXPORT S.A.C.
                    </span>
                  </div>
                </div>

                <!-- QR Info Strip -->
                <div class="d-flex align-center justify-center flex-wrap mt-4 gap-2">
                  <v-chip small outlined :color="isLogoEnabled ? 'primary' : 'grey darken-3'" class="font-weight-medium">
                    <v-icon left x-small>{{ isLogoEnabled ? 'mdi-shield-star' : 'mdi-qrcode' }}</v-icon>
                    {{ isLogoEnabled ? 'Con Logotipo' : 'Sin Logotipo (Estándar)' }}
                  </v-chip>
                  <v-chip v-if="isPresentationCardEnabled" small outlined color="orange darken-2" class="font-weight-medium">
                    <v-icon left x-small>mdi-card-bulleted-outline</v-icon>
                    Modo Ficha Activo
                  </v-chip>
                  <v-chip small outlined color="grey darken-2" class="font-weight-medium">
                    <v-icon left x-small>mdi-tag-outline</v-icon>
                    Tipo: {{ activeContentTypeLabel }}
                  </v-chip>
                  <v-chip small outlined color="grey darken-2" class="font-weight-medium">
                    <v-icon left x-small>mdi-database</v-icon>
                    {{ contentCharacterCount }} carac.
                  </v-chip>
                  <v-chip small outlined color="primary" class="font-weight-medium">
                    <v-icon left x-small>mdi-clock-outline</v-icon>
                    {{ lastUpdatedTime }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Export Actions Toolbar -->
            <v-card-actions class="pa-4 bg-slate-subtle d-flex flex-column gap-2">
              <!-- Primary: Copy Image to Clipboard -->
              <v-btn
                block
                color="primary"
                class="rounded-lg text-none font-weight-bold py-4 white--text"
                elevation="0"
                :disabled="!hasValidContent || isProcessing"
                :loading="isProcessing"
                @click="copyImageToClipboard"
              >
                <v-icon left small>mdi-content-copy</v-icon>
                {{ isPresentationCardEnabled ? 'Copiar Ficha Institucional al Portapapeles' : 'Copiar Imagen al Portapapeles' }}
              </v-btn>

              <v-row dense class="w-100">
                <!-- Download PNG (or Card PNG) -->
                <v-col cols="6">
                  <v-btn
                    block
                    outlined
                    color="grey darken-3"
                    class="rounded-lg text-none font-weight-bold"
                    elevation="0"
                    :disabled="!hasValidContent || isProcessing"
                    @click="downloadPNG(false)"
                  >
                    <v-icon left small color="primary">mdi-file-png-box</v-icon>
                    {{ isPresentationCardEnabled ? 'Descargar Ficha' : 'PNG (600px)' }}
                  </v-btn>
                </v-col>

                <!-- Download PNG Ultra HD (1200px for print) -->
                <v-col cols="6">
                  <v-btn
                    block
                    outlined
                    color="grey darken-3"
                    class="rounded-lg text-none font-weight-bold"
                    elevation="0"
                    :disabled="!hasValidContent || isProcessing"
                    @click="downloadPNG(true)"
                  >
                    <v-icon left small color="orange darken-2">mdi-quality-high</v-icon>
                    {{ isPresentationCardEnabled ? 'Ficha HD (Print)' : 'PNG HD (1200px)' }}
                  </v-btn>
                </v-col>

                <!-- Download SVG Vector -->
                <v-col cols="6">
                  <v-btn
                    block
                    outlined
                    color="grey darken-3"
                    class="rounded-lg text-none font-weight-bold"
                    elevation="0"
                    :disabled="!hasValidContent || isProcessing"
                    @click="downloadSVG"
                  >
                    <v-icon left small color="purple">mdi-vector-square</v-icon>
                    Vector SVG
                  </v-btn>
                </v-col>

                <!-- Print Direct (Dedicated Clean Print) -->
                <v-col cols="6">
                  <v-btn
                    block
                    outlined
                    color="grey darken-3"
                    class="rounded-lg text-none font-weight-bold"
                    elevation="0"
                    :disabled="!hasValidContent || isProcessing"
                    @click="printCard"
                  >
                    <v-icon left small color="grey darken-2">mdi-printer</v-icon>
                    {{ isPresentationCardEnabled ? 'Imprimir Ficha' : 'Imprimir QR' }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import defaultLogoSrc from '@/assets/logoSunDrive.png';

export default {
  name: 'QRGeneratorProFixed',
  data() {
    return {
      // Active content tab
      activeTab: 'text',
      isProcessing: false,
      hasValidContent: true, // starts true with default content
      lastUpdatedTime: '',
      previewSize: 320,

      // Logo Assets & Configuration (Fully Optional)
      defaultLogoUrl: defaultLogoSrc,
      customLogoUrl: null,
      customLogoFileName: '',
      logoSource: 'default', // 'default' | 'custom'
      isLogoEnabled: true,   // Optional toggle (true by default)
      logoSizePercent: 20,   // Default: 20%
      logoBadgeShape: 'rounded', // 'rounded' | 'circle' | 'square'
      logoBadgeBgColor: '#FFFFFF',
      hasLogoBorder: false,  // Default: false (unmarked)
      logoBorderColor: '#E2E8F0',
      logoImageElement: null,
      logoBase64DataUrl: null,

      // Standard Error Correction Level (when logo is disabled)
      errorCorrectionLevel: 'M',
      errorCorrectionOptions: [
        { text: 'Bajo (L - 7% recuperación)', value: 'L' },
        { text: 'Medio (M - 15% recuperación) - Estándar', value: 'M' },
        { text: 'Alto (Q - 25% recuperación)', value: 'Q' },
        { text: 'Máximo (H - 30% recuperación)', value: 'H' }
      ],

      // Color Palette Settings (Default: Classic Black)
      foregroundColor: '#000000', // Default: Classic Black
      backgroundColor: '#FFFFFF',
      colorPresets: [
        { label: 'Negro Clásico', hex: '#000000' },
        { label: 'Naranja Sunshine', hex: '#F26522' },
        { label: 'Gris Pizarra', hex: '#1E293B' },
        { label: 'Azul Marino', hex: '#0F3E6D' },
        { label: 'Verde Éxito', hex: '#059669' }
      ],

      // Shape Options
      logoShapeOptions: [
        { text: 'Bordes Redondeados (Suave)', value: 'rounded' },
        { text: 'Circular', value: 'circle' },
        { text: 'Cuadrado Limpio', value: 'square' }
      ],

      // Content Inputs: Text
      textInput: 'SUNSHINE EXPORT S.A.C. - Sistema SunExpert',

      // Content Inputs: URL
      urlInput: 'https://sunshine.com.pe',

      // Content Inputs: vCard Contact
      contactFirstName: '',
      contactLastName: '',
      contactPhone: '',
      contactEmail: '',
      contactOrg: 'SUNSHINE EXPORT S.A.C.',
      contactCargo: '',

      // Content Inputs: Wi-Fi
      wifiSSID: 'Sunshine-Visitas',
      wifiPassword: '',
      wifiSecurity: 'WPA',
      wifiHidden: false,
      showWifiPassword: false,
      wifiSecurityOptions: [
        { text: 'WPA / WPA2 / WPA3 (Estándar)', value: 'WPA' },
        { text: 'WEP', value: 'WEP' },
        { text: 'Red Abierta (Sin contraseña)', value: 'nopass' }
      ],

      // Content Inputs: WhatsApp
      waCountryCode: '51',
      waPhoneNumber: '',
      waMessage: 'Hola, me comunico a través del código QR institucional.',

      // Content Inputs: Email
      emailAddress: '',
      emailSubject: 'Consulta Sunshine',
      emailBody: '',

      // Presentation Card Configuration
      isPresentationCardEnabled: false,
      cardTitle: 'SUNSHINE EXPORT S.A.C.',
      cardSubtitle: 'Escanee con su cámara móvil para acceder',

      // Debounce Timer ID
      debounceRenderTimer: null
    };
  },

  computed: {
    activeLogoSrc() {
      if (this.logoSource === 'custom' && this.customLogoUrl) {
        return this.customLogoUrl;
      }
      return this.defaultLogoUrl;
    },

    activeContentTypeLabel() {
      switch (this.activeTab) {
        case 'text':
          return 'Texto Libre';
        case 'url':
          return 'Enlace Web (URL)';
        case 'contact':
          return 'Tarjeta vCard';
        case 'wifi':
          return 'Conexión Wi-Fi';
        case 'whatsapp':
          return 'Mensaje WhatsApp';
        case 'email':
          return 'Correo Electrónico';
        default:
          return 'Código QR';
      }
    },

    generatedContentString() {
      switch (this.activeTab) {
        case 'text':
          return (this.textInput || '').trim();

        case 'url': {
          const rawUrl = (this.urlInput || '').trim();
          if (!rawUrl) return '';
          if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
            return `https://${rawUrl}`;
          }
          return rawUrl;
        }

        case 'contact': {
          const fullName = `${this.contactFirstName || ''} ${this.contactLastName || ''}`.trim();
          if (!fullName && !this.contactPhone && !this.contactEmail) return '';

          return [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `N:${(this.contactLastName || '').trim()};${(this.contactFirstName || '').trim()};;;`,
            `FN:${fullName}`,
            `ORG:${(this.contactOrg || 'SUNSHINE EXPORT S.A.C.').trim()}`,
            `TITLE:${(this.contactCargo || '').trim()}`,
            `TEL;TYPE=CELL:${(this.contactPhone || '').trim()}`,
            `EMAIL;TYPE=INTERNET:${(this.contactEmail || '').trim()}`,
            'END:VCARD'
          ].join('\n');
        }

        case 'wifi': {
          const ssid = (this.wifiSSID || '').trim();
          if (!ssid) return '';
          const sec = this.wifiSecurity || 'WPA';
          const pass = sec === 'nopass' ? '' : (this.wifiPassword || '').trim();
          const hidden = this.wifiHidden ? 'H:true;' : '';
          return `WIFI:T:${sec};S:${ssid};P:${pass};${hidden};`;
        }

        case 'whatsapp': {
          const cleanPhone = (this.waPhoneNumber || '').replace(/\D/g, '');
          if (!cleanPhone) return '';
          const country = (this.waCountryCode || '51').replace(/\D/g, '');
          const fullNum = `${country}${cleanPhone}`;
          const encodedMsg = encodeURIComponent((this.waMessage || '').trim());
          return `https://wa.me/${fullNum}?text=${encodedMsg}`;
        }

        case 'email': {
          const to = (this.emailAddress || '').trim();
          if (!to) return '';
          const subject = encodeURIComponent((this.emailSubject || '').trim());
          const body = encodeURIComponent((this.emailBody || '').trim());
          let uri = `mailto:${to}`;
          const params = [];
          if (subject) params.push(`subject=${subject}`);
          if (body) params.push(`body=${body}`);
          if (params.length > 0) uri += `?${params.join('&')}`;
          return uri;
        }

        default:
          return '';
      }
    },

    contentCharacterCount() {
      return (this.generatedContentString || '').length;
    }
  },

  watch: {
    activeTab() {
      this.triggerDebouncedRender();
    },
    textInput() {
      this.triggerDebouncedRender();
    },
    urlInput() {
      this.triggerDebouncedRender();
    },
    contactFirstName() {
      this.triggerDebouncedRender();
    },
    contactLastName() {
      this.triggerDebouncedRender();
    },
    contactPhone() {
      this.triggerDebouncedRender();
    },
    contactEmail() {
      this.triggerDebouncedRender();
    },
    contactOrg() {
      this.triggerDebouncedRender();
    },
    contactCargo() {
      this.triggerDebouncedRender();
    },
    wifiSSID() {
      this.triggerDebouncedRender();
    },
    wifiPassword() {
      this.triggerDebouncedRender();
    },
    wifiSecurity() {
      this.triggerDebouncedRender();
    },
    wifiHidden() {
      this.triggerDebouncedRender();
    },
    waPhoneNumber() {
      this.triggerDebouncedRender();
    },
    waCountryCode() {
      this.triggerDebouncedRender();
    },
    waMessage() {
      this.triggerDebouncedRender();
    },
    emailAddress() {
      this.triggerDebouncedRender();
    },
    emailSubject() {
      this.triggerDebouncedRender();
    },
    emailBody() {
      this.triggerDebouncedRender();
    },
    isLogoEnabled() {
      this.$nextTick(() => {
        this.renderQRCode();
      });
    },
    errorCorrectionLevel() {
      this.triggerDebouncedRender();
    },
    logoSizePercent() {
      this.triggerDebouncedRender();
    },
    logoBadgeShape() {
      this.triggerDebouncedRender();
    },
    hasLogoBorder() {
      this.triggerDebouncedRender();
    },
    foregroundColor() {
      this.triggerDebouncedRender();
    },
    backgroundColor() {
      this.triggerDebouncedRender();
    },
    isPresentationCardEnabled() {
      this.$nextTick(() => {
        this.renderQRCode();
      });
    },
    cardTitle() {
      this.$nextTick(() => {
        this.renderQRCode();
      });
    },
    cardSubtitle() {
      this.$nextTick(() => {
        this.renderQRCode();
      });
    },
    logoSource() {
      this.loadActiveLogo();
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.loadActiveLogo();
      this.renderQRCode();
    });
  },

  methods: {
    /**
     * Preloads the active logo and caches it in memory as an HTMLImageElement
     * and as a base64 DataURL for SVG embedding.
     */
    loadActiveLogo() {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      const onReady = () => {
        this.logoImageElement = img;
        this.cacheLogoAsBase64(img);
        this.$nextTick(() => {
          this.renderQRCode();
        });
      };

      img.onload = onReady;
      img.onerror = () => {
        console.error('Failed to load logo image:', this.activeLogoSrc);
      };
      img.src = this.activeLogoSrc;

      if (img.complete) {
        onReady();
      }
    },

    /**
     * Converts the loaded image element to a base64 Data URL string
     */
    cacheLogoAsBase64(img) {
      try {
        const offCanvas = document.createElement('canvas');
        offCanvas.width = img.naturalWidth || img.width || 128;
        offCanvas.height = img.naturalHeight || img.height || 128;
        const ctx = offCanvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        this.logoBase64DataUrl = offCanvas.toDataURL('image/png');
      } catch (err) {
        console.warn('Could not serialize logo to DataURL:', err);
      }
    },

    /**
     * Handles custom logo file selection from user's computer
     */
    onCustomLogoSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.$fun.alert('Por favor seleccione un archivo de imagen válido (PNG, JPG, SVG).', 'warning');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.customLogoUrl = e.target.result;
        this.customLogoFileName = file.name;
        this.logoSource = 'custom';
        this.loadActiveLogo();
        this.$fun.alert('Logotipo personalizado cargado con éxito.', 'success');
      };
      reader.readAsDataURL(file);
    },

    /**
     * Quick selection helper for official brand colors
     */
    applyColorPreset(hex) {
      this.foregroundColor = hex;
    },

    /**
     * Quick helper for URL protocol prefixes
     */
    appendUrlPrefix(prefix) {
      if (!this.urlInput) {
        this.urlInput = prefix;
      } else if (!this.urlInput.startsWith('http://') && !this.urlInput.startsWith('https://')) {
        this.urlInput = `${prefix}${this.urlInput}`;
      }
    },

    /**
     * Debounced wrapper around renderQRCode to ensure smooth typing experience
     */
    triggerDebouncedRender() {
      clearTimeout(this.debounceRenderTimer);
      this.debounceRenderTimer = setTimeout(() => {
        this.renderQRCode();
      }, 150);
    },

    /**
     * Main rendering routine that outputs to the preview canvas
     */
    async renderQRCode(targetSize = null) {
      const content = this.generatedContentString;
      if (!content) {
        this.hasValidContent = false;
        return;
      }

      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;

      const size = targetSize || this.previewSize;
      const qrLib = QRCode.default || QRCode;

      // When logo is enabled, enforce High (H: 30%) error correction for 100% scan reliability
      // When logo is disabled, respect the selected error correction level (L, M, Q, H)
      const qrOptions = {
        errorCorrectionLevel: this.isLogoEnabled ? 'H' : this.errorCorrectionLevel,
        margin: 2,
        width: size,
        color: {
          dark: this.foregroundColor,
          light: this.backgroundColor
        }
      };

      try {
        await qrLib.toCanvas(canvas, content, qrOptions);

        if (this.isLogoEnabled && this.logoImageElement) {
          this.drawLogoBadgeOnCanvas(canvas, size);
        }

        this.hasValidContent = true;
        this.lastUpdatedTime = new Date().toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Error generating QR Canvas:', error);
      }
    },

    /**
     * Draws the protective badge background and center corporate logo on canvas
     */
    drawLogoBadgeOnCanvas(canvas, canvasSize) {
      const ctx = canvas.getContext('2d');
      const centerX = canvasSize / 2;
      const centerY = canvasSize / 2;

      const logoDim = Math.round(canvasSize * (this.logoSizePercent / 100));
      const badgePadding = Math.round(logoDim * 0.14);
      const badgeDim = logoDim + badgePadding * 2;
      const halfBadge = badgeDim / 2;

      ctx.save();

      // 1. Draw protective badge shape
      if (this.logoBadgeShape === 'circle') {
        ctx.beginPath();
        ctx.arc(centerX, centerY, halfBadge, 0, Math.PI * 2);
        ctx.fillStyle = this.logoBadgeBgColor;
        ctx.fill();

        if (this.hasLogoBorder) {
          ctx.lineWidth = Math.max(1, Math.round(canvasSize * 0.007));
          ctx.strokeStyle = this.logoBorderColor;
          ctx.stroke();
        }
      } else if (this.logoBadgeShape === 'rounded') {
        const rx = centerX - halfBadge;
        const ry = centerY - halfBadge;
        const radius = Math.round(badgeDim * 0.22);

        ctx.beginPath();
        ctx.moveTo(rx + radius, ry);
        ctx.lineTo(rx + badgeDim - radius, ry);
        ctx.quadraticCurveTo(rx + badgeDim, ry, rx + badgeDim, ry + radius);
        ctx.lineTo(rx + badgeDim, ry + badgeDim - radius);
        ctx.quadraticCurveTo(rx + badgeDim, ry + badgeDim, rx + badgeDim - radius, ry + badgeDim);
        ctx.lineTo(rx + radius, ry + badgeDim);
        ctx.quadraticCurveTo(rx, ry + badgeDim, rx, ry + badgeDim - radius);
        ctx.lineTo(rx, ry + radius);
        ctx.quadraticCurveTo(rx, ry, rx + radius, ry);
        ctx.closePath();

        ctx.fillStyle = this.logoBadgeBgColor;
        ctx.fill();

        if (this.hasLogoBorder) {
          ctx.lineWidth = Math.max(1, Math.round(canvasSize * 0.007));
          ctx.strokeStyle = this.logoBorderColor;
          ctx.stroke();
        }
      } else {
        // Square shape
        const rx = centerX - halfBadge;
        const ry = centerY - halfBadge;
        ctx.fillStyle = this.logoBadgeBgColor;
        ctx.fillRect(rx, ry, badgeDim, badgeDim);

        if (this.hasLogoBorder) {
          ctx.lineWidth = Math.max(1, Math.round(canvasSize * 0.007));
          ctx.strokeStyle = this.logoBorderColor;
          ctx.strokeRect(rx, ry, badgeDim, badgeDim);
        }
      }

      // 2. Draw centered logo image
      const logoX = centerX - logoDim / 2;
      const logoY = centerY - logoDim / 2;
      ctx.drawImage(this.logoImageElement, logoX, logoY, logoDim, logoDim);

      ctx.restore();
    },

    /**
     * Builds an independent high-resolution canvas instance of the QR code
     */
    async generateQrCanvas(targetResolution) {
      const content = this.generatedContentString;
      if (!content) return null;

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = targetResolution;
      exportCanvas.height = targetResolution;

      const qrLib = QRCode.default || QRCode;
      const qrOptions = {
        errorCorrectionLevel: this.isLogoEnabled ? 'H' : this.errorCorrectionLevel,
        margin: 2,
        width: targetResolution,
        color: {
          dark: this.foregroundColor,
          light: this.backgroundColor
        }
      };

      await qrLib.toCanvas(exportCanvas, content, qrOptions);

      if (this.isLogoEnabled && this.logoImageElement) {
        this.drawLogoBadgeOnCanvas(exportCanvas, targetResolution);
      }

      return exportCanvas;
    },

    /**
     * Captures the full presentation card or standalone QR canvas as an image Blob
     */
    async captureExportBlob(isHighRes = false) {
      if (this.isPresentationCardEnabled) {
        const cardElement = document.getElementById('printableQrSection');
        if (!cardElement) throw new Error('No se encontró el elemento de la ficha.');

        // Use html2canvas to render the entire institutional card
        const cardCanvas = await html2canvas(cardElement, {
          scale: isHighRes ? 3 : 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        return new Promise((resolve) => {
          cardCanvas.toBlob((blob) => resolve(blob), 'image/png');
        });
      } else {
        const qrCanvas = await this.generateQrCanvas(isHighRes ? 1200 : 600);
        if (!qrCanvas) return null;

        return new Promise((resolve) => {
          qrCanvas.toBlob((blob) => resolve(blob), 'image/png');
        });
      }
    },

    /**
     * Copies either the institutional card or standard QR image to system clipboard
     */
    async copyImageToClipboard() {
      if (!this.hasValidContent) {
        this.$fun.alert('No hay código QR para copiar.', 'warning');
        return;
      }

      this.isProcessing = true;
      try {
        const blob = await this.captureExportBlob(false);
        if (!blob) throw new Error('No se pudo generar la imagen para copiar.');

        if (navigator.clipboard && window.ClipboardItem) {
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          const msg = this.isPresentationCardEnabled
            ? '¡Ficha Institucional copiada al portapapeles! Puedes pegarla con Ctrl + V.'
            : '¡Código QR copiado al portapapeles! Puedes pegarlo con Ctrl + V.';
          this.$fun.alert(msg, 'success');
        } else {
          this.$fun.alert('Tu navegador no admite copiado directo al portapapeles. Usa la opción de descarga.', 'warning');
        }
      } catch (err) {
        console.error('Copy to clipboard failed:', err);
        this.$fun.alert('No se pudo copiar automáticamente. Use el botón de descarga.', 'warning');
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Downloads PNG image (Full Presentation Card if enabled, or Standalone QR)
     */
    async downloadPNG(isHighRes = false) {
      if (!this.hasValidContent) {
        this.$fun.alert('No hay código QR para descargar.', 'warning');
        return;
      }

      this.isProcessing = true;
      try {
        const blob = await this.captureExportBlob(isHighRes);
        if (!blob) throw new Error('Error al generar la imagen para descargar.');

        const prefix = this.isPresentationCardEnabled ? 'ficha_sunshine_' : 'qr_sunshine_';
        const fileName = `${prefix}${isHighRes ? 'hd_' : ''}${Date.now()}.png`;
        this.triggerDownloadBlob(blob, fileName);

        const successMsg = this.isPresentationCardEnabled
          ? 'Ficha institucional descargada en formato PNG exitosamente.'
          : 'Código QR descargado exitosamente.';
        this.$fun.alert(successMsg, 'success');
      } catch (err) {
        console.error('Download PNG failed:', err);
        this.$fun.alert('Error al descargar el archivo PNG.', 'error');
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Generates and downloads vector SVG
     */
    async downloadSVG() {
      if (!this.hasValidContent) {
        this.$fun.alert('No hay código QR para descargar.', 'warning');
        return;
      }

      this.isProcessing = true;
      try {
        const qrLib = QRCode.default || QRCode;
        const options = {
          errorCorrectionLevel: this.isLogoEnabled ? 'H' : this.errorCorrectionLevel,
          margin: 2,
          width: 600,
          color: {
            dark: this.foregroundColor,
            light: this.backgroundColor
          }
        };

        let svgString = await qrLib.toString(this.generatedContentString, { ...options, type: 'svg' });

        // If logo is enabled, inject SVG elements (badge and logo image)
        if (this.isLogoEnabled && this.logoBase64DataUrl) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgString, 'image/svg+xml');
          const svgEl = doc.documentElement;
          const viewBox = svgEl.getAttribute('viewBox') || '0 0 600 600';
          const parts = viewBox.split(' ').map(Number);
          const vbWidth = parts[2] || 600;
          const vbHeight = parts[3] || 600;

          const cx = vbWidth / 2;
          const cy = vbHeight / 2;
          const logoDim = Math.round(vbWidth * (this.logoSizePercent / 100));
          const badgePadding = Math.round(logoDim * 0.14);
          const badgeDim = logoDim + badgePadding * 2;
          const halfBadge = badgeDim / 2;

          // 1. Badge shape element
          if (this.logoBadgeShape === 'circle') {
            const circle = doc.createElementNS('http://www.w3.org/2000/svg', 'circle');
            circle.setAttribute('cx', String(cx));
            circle.setAttribute('cy', String(cy));
            circle.setAttribute('r', String(halfBadge));
            circle.setAttribute('fill', this.logoBadgeBgColor);
            if (this.hasLogoBorder) {
              circle.setAttribute('stroke', this.logoBorderColor);
              circle.setAttribute('stroke-width', String(Math.max(1, Math.round(vbWidth * 0.007))));
            }
            svgEl.appendChild(circle);
          } else {
            const rect = doc.createElementNS('http://www.w3.org/2000/svg', 'rect');
            rect.setAttribute('x', String(cx - halfBadge));
            rect.setAttribute('y', String(cy - halfBadge));
            rect.setAttribute('width', String(badgeDim));
            rect.setAttribute('height', String(badgeDim));
            if (this.logoBadgeShape === 'rounded') {
              rect.setAttribute('rx', String(Math.round(badgeDim * 0.22)));
              rect.setAttribute('ry', String(Math.round(badgeDim * 0.22)));
            }
            rect.setAttribute('fill', this.logoBadgeBgColor);
            if (this.hasLogoBorder) {
              rect.setAttribute('stroke', this.logoBorderColor);
              rect.setAttribute('stroke-width', String(Math.max(1, Math.round(vbWidth * 0.007))));
            }
            svgEl.appendChild(rect);
          }

          // 2. Embedded Logo Image
          const imgEl = doc.createElementNS('http://www.w3.org/2000/svg', 'image');
          imgEl.setAttribute('x', String(cx - logoDim / 2));
          imgEl.setAttribute('y', String(cy - logoDim / 2));
          imgEl.setAttribute('width', String(logoDim));
          imgEl.setAttribute('height', String(logoDim));
          imgEl.setAttribute('href', this.logoBase64DataUrl);
          svgEl.appendChild(imgEl);

          svgString = new XMLSerializer().serializeToString(svgEl);
        }

        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        this.triggerDownloadBlob(blob, `qr_sunshine_${Date.now()}.svg`);
        this.$fun.alert('Código QR SVG vectorial descargado exitosamente.', 'success');
      } catch (err) {
        console.error('Download SVG failed:', err);
        this.$fun.alert('Error al descargar el archivo vectorial SVG.', 'error');
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Browser download helper
     */
    triggerDownloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    /**
     * Dedicated Clean Print Window (Prints ONLY the card or QR, never the whole page)
     */
    printCard() {
      if (!this.hasValidContent) {
        this.$fun.alert('No hay código QR para imprimir.', 'warning');
        return;
      }

      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;

      const qrDataUrl = canvas.toDataURL('image/png');
      const logoSrc = this.activeLogoSrc;
      const title = this.cardTitle || 'SUNSHINE EXPORT S.A.C.';
      const subtitle = this.cardSubtitle || 'Escanee con su cámara móvil para acceder';
      const isCard = this.isPresentationCardEnabled;

      const printWindow = window.open('', '_blank', 'width=750,height=850');
      if (!printWindow) {
        this.$fun.alert('Por favor habilite las ventanas emergentes en su navegador para imprimir.', 'warning');
        return;
      }

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Imprimir QR - SUNSHINE EXPORT S.A.C.</title>
            <style>
              * { box-sizing: border-box; }
              body {
                margin: 0;
                padding: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
                background-color: #ffffff;
                color: #1e293b;
              }
              .print-container {
                border: ${isCard ? '2px solid #cbd5e1' : 'none'};
                border-radius: 16px;
                padding: ${isCard ? '28px 36px' : '10px'};
                text-align: center;
                max-width: 440px;
                width: 100%;
                background: #ffffff;
                margin: 0 auto;
              }
              .logo-img {
                max-height: 46px;
                width: auto;
                margin: 0 auto 8px auto;
                display: block;
              }
              .card-title {
                font-size: 19px;
                font-weight: 800;
                margin: 0 0 4px 0;
                color: #0f172a;
              }
              .card-subtitle {
                font-size: 13px;
                color: #64748b;
                margin: 0 0 16px 0;
                line-height: 1.3;
              }
              .qr-image {
                max-width: 320px;
                width: 100%;
                height: auto;
                display: block;
                margin: 0 auto;
                border-radius: 8px;
              }
              .card-footer {
                margin-top: 18px;
                padding-top: 12px;
                border-top: 1px dashed #cbd5e1;
                font-size: 11px;
                font-weight: 800;
                letter-spacing: 0.1em;
                color: #334155;
                text-transform: uppercase;
              }
              @media print {
                body {
                  padding: 0;
                  margin: 0;
                  min-height: auto;
                }
                .print-container {
                  border: ${isCard ? '1.5px solid #000' : 'none'};
                  margin: 20px auto;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              ${isCard ? `
                <img src="${logoSrc}" class="logo-img" alt="Sunshine Logo" />
                <h2 class="card-title">${title}</h2>
                <p class="card-subtitle">${subtitle}</p>
              ` : ''}
              <img src="${qrDataUrl}" class="qr-image" alt="QR Code" />
              ${isCard ? `
                <div class="card-footer">SUNSHINE EXPORT S.A.C.</div>
              ` : ''}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 300);
              };
            <\/script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    },

    /**
     * Resets input fields to empty state
     */
    resetAllFields() {
      this.$fun.alert('¿Está seguro de limpiar todos los datos del formulario?', 'question').then((r) => {
        if (r && r.value) {
          this.textInput = '';
          this.urlInput = '';
          this.contactFirstName = '';
          this.contactLastName = '';
          this.contactPhone = '';
          this.contactEmail = '';
          this.contactOrg = 'SUNSHINE EXPORT S.A.C.';
          this.contactCargo = '';
          this.wifiSSID = '';
          this.wifiPassword = '';
          this.waPhoneNumber = '';
          this.waMessage = '';
          this.emailAddress = '';
          this.emailSubject = '';
          this.emailBody = '';
          this.hasValidContent = false;
          this.$fun.alert('Formulario limpiado con éxito.', 'success');
        }
      });
    }
  }
};
</script>

<style scoped>
.qr-generator-view {
  min-height: 100vh;
  background-color: #f8fafc;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-subtle {
  border: 1px solid #edf2f7;
}

.border-top-light {
  border-top: 1px solid #e2e8f0;
}

.bg-slate-subtle {
  background-color: #f8fafc;
}

.header-logo {
  max-height: 32px;
  width: auto;
  display: block;
}

.custom-tabs >>> .v-tabs-bar {
  background-color: transparent !important;
  border-bottom: 1px solid #e2e8f0;
}

.custom-tabs >>> .v-tab {
  text-transform: none;
  font-size: 0.825rem;
  font-weight: 600;
  min-width: auto;
  letter-spacing: normal;
}

.color-picker-input {
  width: 38px;
  height: 38px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  padding: 2px;
}

.font-mono {
  font-family: monospace;
}

.gap-2 {
  gap: 8px;
}

.cursor-pointer {
  cursor: pointer;
}

/* Sticky Preview Container on Desktop */
@media (min-width: 960px) {
  .sticky-preview-container {
    position: sticky;
    top: 20px;
    z-index: 2;
  }
}

.empty-preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-presentation-box {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  display: inline-block;
  max-width: 100%;
}

.card-presentation-mode {
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  padding: 1.75rem;
}

.presentation-logo {
  max-height: 40px;
  width: auto;
  margin: 0 auto;
  display: block;
}

.presentation-footer {
  border-top: 1px dashed #cbd5e1;
}

.tracking-wider {
  letter-spacing: 0.08em;
  font-size: 0.72rem;
}

.canvas-shadow-holder {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-canvas {
  max-width: 100%;
  height: auto !important;
  border-radius: 8px;
  display: block;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}
</style>